import { SecurePassphraseStorage, prepareUserCryptoInBackground } from "./matrixClientManager";
import {
    BACKUP_STORAGE_KEYS,
    removeBackupStorageValue,
    setBackupStorageValue
} from "./matrixBackupStorage";

export const CHAT_BACKUP_SETUP_RESULT = {
    CREATED: "created",
    RESTORED: "restored",
    SKIPPED: "skipped"
};

export const attemptChatBackupWithLoginPassphrase = async (matrixClient, passphrase) => {
    const normalizedPassphrase = typeof passphrase === "string" ? passphrase : "";
    if (!normalizedPassphrase.trim()) {
        return CHAT_BACKUP_SETUP_RESULT.SKIPPED;
    }

    const crypto = matrixClient?.getCrypto?.();
    if (!crypto) {
        return CHAT_BACKUP_SETUP_RESULT.SKIPPED;
    }

    const backupInfo = await crypto.getKeyBackupInfo();
    if (backupInfo?.auth_data) {
        await restoreChatBackupWithPassphrase(matrixClient, normalizedPassphrase, backupInfo);
        return CHAT_BACKUP_SETUP_RESULT.RESTORED;
    }

    await createChatBackupWithPassphrase(matrixClient, normalizedPassphrase);
    return CHAT_BACKUP_SETUP_RESULT.CREATED;
};

export const createChatBackupWithPassphrase = async (matrixClient, passphrase) => {
    const normalizedPassphrase = typeof passphrase === "string" ? passphrase : "";
    if (!normalizedPassphrase.trim()) {
        throw new Error("A backup password is required.");
    }

    const crypto = matrixClient?.getCrypto?.();
    if (!crypto) {
        throw new Error("Matrix encryption is not ready for backup creation.");
    }

    try {
        SecurePassphraseStorage.set(normalizedPassphrase, matrixClient);
        const cryptoIsReady = await prepareUserCryptoInBackground(matrixClient, normalizedPassphrase, {
            force: true,
            setupNewKeyBackup: true,
            setupNewSecretStorage: true,
        });
        if (!cryptoIsReady) {
            throw new Error("Matrix crypto backup setup failed.");
        }
        const backupInfo = await crypto.getKeyBackupInfo();
        if (!backupInfo) {
            throw new Error("Matrix backup creation could not be verified.");
        }
        await verifyCreatedBackup(crypto, backupInfo);

        removeBackupStorageValue(BACKUP_STORAGE_KEYS.BACKUP_MODAL_ACTIONED, matrixClient);
        setBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient);
        return CHAT_BACKUP_SETUP_RESULT.CREATED;
    } catch (error) {
        SecurePassphraseStorage.clear(matrixClient);
        removeBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient);
        throw error;
    }
};

export const restoreChatBackupWithPassphrase = async (matrixClient, passphrase, backupInfo) => {
    const normalizedPassphrase = typeof passphrase === "string" ? passphrase : "";
    if (!normalizedPassphrase.trim()) {
        return CHAT_BACKUP_SETUP_RESULT.SKIPPED;
    }

    const crypto = matrixClient?.getCrypto?.();
    if (!crypto) {
        return CHAT_BACKUP_SETUP_RESULT.SKIPPED;
    }

    try {
        const backupInfoToUse = backupInfo || await crypto.getKeyBackupInfo();
        if (!backupInfoToUse) {
            throw new Error("No key backup found.");
        }

        SecurePassphraseStorage.set(normalizedPassphrase, matrixClient);
        const result = await restoreKeyBackupWithPassphrase(crypto, normalizedPassphrase, backupInfoToUse);
        await prepareUserCryptoInBackground(matrixClient, normalizedPassphrase, {
            force: true,
            allowSecretStorageFailure: true,
        });
        await enableKeyBackupIfAvailable(crypto);
        await verifyRestoredBackup(crypto, backupInfoToUse);
        setBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient);
        return result;
    } catch (error) {
        SecurePassphraseStorage.clear(matrixClient);
        removeBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient);
        throw error;
    }
};

const verifyCreatedBackup = async (crypto, backupInfo) => {
    const crossSigningIsReady = await crypto.isCrossSigningReady();
    const activeBackupVersion = await crypto.getActiveSessionBackupVersion();
    if (!crossSigningIsReady || !activeBackupVersion || activeBackupVersion !== backupInfo.version) {
        throw new Error("Matrix backup creation verification failed.");
    }
};

const verifyRestoredBackup = async (crypto, restoredBackupInfo) => {
    const currentBackupInfo = await crypto.getKeyBackupInfo();
    if (!currentBackupInfo?.version || currentBackupInfo.version !== restoredBackupInfo.version) {
        throw new Error("The Matrix backup version changed during restoration.");
    }
};

export const restoreKeyBackupWithPassphrase = async (crypto, passphrase, backupInfo) => {
    try {
        await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
    } catch (secretStorageError) {
        if (!backupSupportsPassphrase(backupInfo)) {
            throw secretStorageError;
        }
        console.warn("[Matrix] Secret storage backup key load failed, falling back to passphrase restore:", secretStorageError);
        return await crypto.restoreKeyBackupWithPassphrase(passphrase);
    }

    try {
        return await crypto.restoreKeyBackup();
    } catch (restoreError) {
        if (!backupSupportsPassphrase(backupInfo) || !backupDecryptionKeyDoesNotMatch(restoreError)) {
            throw restoreError;
        }
        console.warn("[Matrix] Secret storage contains a stale backup key; trying the supplied passphrase.");
        return await crypto.restoreKeyBackupWithPassphrase(passphrase);
    }
};

export const backupPasswordIsIncorrect = (error) => {
    const errorText = `${error?.message || ""} ${error?.name || ""} ${error?.code || ""}`;
    return /bad mac|incorrect|invalid passphrase|wrong passphrase|authentication failed|mismatched backup|wrong backup|does not match the decryption key|decryption key does not match backup/i.test(errorText);
};

const backupSupportsPassphrase = (backupInfo) => {
    return !!backupInfo?.auth_data?.private_key_salt && !!backupInfo?.auth_data?.private_key_iterations;
};

const backupDecryptionKeyDoesNotMatch = (error) => {
    return /backup on server does not match the decryption key|decryption key does not match backup/i
        .test(error?.message || "");
};

const enableKeyBackupIfAvailable = async (crypto) => {
    try {
        await crypto.checkKeyBackupAndEnable();
    } catch (error) {
        console.warn("[Matrix] Backup restored, but key backup enable check failed:", error);
    }
};
